import { fetchData } from './apis';

// ==================== AI Config API Service ====================
// API layer for Nexa AI Sales Agent configuration

export const aiConfigApi = {
  // ==================== CONFIG ENDPOINTS ====================

  /**
   * Get full AI config
   * GET /retrieval/config?companyId=1
   */
  getConfig: (companyId) => {
    return fetchData({
      url: `retrieval/config?companyId=${companyId}`,
      method: 'GET',
    });
  },

  /**
   * Update AI config
   * PUT /retrieval/config
   */
  updateConfig: (data) => {
    return fetchData({
      url: 'retrieval/config',
      method: 'PUT',
      data,
    });
  },

  /**
   * Get product catalog vectors currently indexed for an Agent.
   * GET /ingestion/product-knowledge/stats?companyId=1
   */
  getProductKnowledgeStats: (companyId) => {
    return fetchData({
      url: `ingestion/product-knowledge/stats?companyId=${companyId}`,
      method: 'GET',
    });
  },

  // ==================== SALES STAGES ENDPOINTS ====================

  /**
   * Get all stages for a company
   * GET /retrieval/stages?companyId=1
   */
  getStages: (companyId) => {
    return fetchData({
      url: `retrieval/stages?companyId=${companyId}`,
      method: 'GET',
    });
  },

  /**
   * Get all stages including inactive
   * GET /retrieval/stages/all?companyId=1
   */
  getAllStages: (companyId) => {
    return fetchData({
      url: `retrieval/stages/all?companyId=${companyId}`,
      method: 'GET',
    });
  },

  /**
   * Create or update a stage
   * POST /retrieval/stages
   */
  createStage: (data) => {
    return fetchData({
      url: 'retrieval/stages',
      method: 'POST',
      data,
    });
  },

  /**
   * Update a stage
   * PUT /retrieval/stages/:id
   */
  updateStage: (id, data) => {
    return fetchData({
      url: `retrieval/stages/${id}`,
      method: 'PUT',
      data,
    });
  },

  /**
   * Delete a stage (soft delete)
   * DELETE /retrieval/stages/:id
   */
  deleteStage: (id) => {
    return fetchData({
      url: `retrieval/stages/${id}`,
      method: 'DELETE',
    });
  },

  /**
   * Reorder stages
   * PUT /retrieval/stages/reorder
   */
  reorderStages: (data) => {
    return fetchData({
      url: 'retrieval/stages/reorder',
      method: 'PUT',
      data,
    });
  },

  /**
   * Initialize default stages for a company
   * POST /retrieval/stages/init?companyId=1
   */
  initStages: (companyId) => {
    return fetchData({
      url: `retrieval/stages/init?companyId=${companyId}`,
      method: 'POST',
    });
  },

  // ==================== FAQ ENDPOINTS ====================

  /**
   * Get all FAQs for a company
   * GET /retrieval/faq?companyId=1&includeInactive=false
   */
  getFAQs: (companyId, includeInactive = false) => {
    return fetchData({
      url: `retrieval/faq?companyId=${companyId}&includeInactive=${includeInactive}`,
      method: 'GET',
    });
  },

  /**
   * Get FAQ statistics
   * GET /retrieval/faq/stats?companyId=1
   */
  getFAQStats: (companyId) => {
    return fetchData({
      url: `retrieval/faq/stats?companyId=${companyId}`,
      method: 'GET',
    });
  },

  /**
   * Create a new FAQ
   * POST /retrieval/faq
   */
  createFAQ: (data) => {
    return fetchData({
      url: 'retrieval/faq',
      method: 'POST',
      data,
    });
  },

  /**
   * Update a FAQ
   * PUT /retrieval/faq/:id
   */
  updateFAQ: (id, data) => {
    return fetchData({
      url: `retrieval/faq/${id}`,
      method: 'PUT',
      data,
    });
  },

  /**
   * Delete a FAQ
   * DELETE /retrieval/faq/:id
   */
  deleteFAQ: (id) => {
    return fetchData({
      url: `retrieval/faq/${id}`,
      method: 'DELETE',
    });
  },

  /**
   * Reorder FAQs by priority
   * PUT /retrieval/faq/reorder
   */
  reorderFAQs: (data) => {
    return fetchData({
      url: 'retrieval/faq/reorder',
      method: 'PUT',
      data,
    });
  },

  // ==================== VERSION ENDPOINTS ====================

  /**
   * Get all versions for a company
   * GET /retrieval/versions?companyId=1
   */
  getVersions: (companyId) => {
    return fetchData({
      url: `retrieval/versions?companyId=${companyId}`,
      method: 'GET',
    });
  },

  /**
   * Get a specific version
   * GET /retrieval/versions/:id
   */
  getVersion: (id) => {
    return fetchData({
      url: `retrieval/versions/${id}`,
      method: 'GET',
    });
  },

  /**
   * Get current active version
   * GET /retrieval/versions/current?companyId=1
   */
  getCurrentVersion: (companyId) => {
    return fetchData({
      url: `retrieval/versions/current?companyId=${companyId}`,
      method: 'GET',
    });
  },

  /**
   * Publish a new version
   * POST /retrieval/versions/publish
   */
  publishVersion: (data) => {
    return fetchData({
      url: 'retrieval/versions/publish',
      method: 'POST',
      data,
    });
  },

  /**
   * Rollback to a specific version
   * POST /retrieval/versions/:id/rollback
   */
  rollbackVersion: (id, data) => {
    return fetchData({
      url: `retrieval/versions/${id}/rollback`,
      method: 'POST',
      data,
    });
  },

  /**
   * Get diff between two versions
   * GET /retrieval/versions/diff?companyId=1&from=1&to=2
   */
  getVersionDiff: (companyId, fromId, toId) => {
    return fetchData({
      url: `retrieval/versions/diff?companyId=${companyId}&from=${fromId}&to=${toId}`,
      method: 'GET',
    });
  },

  // ==================== FAQ IMPORT SESSION ENDPOINTS ====================

  createFaqImportSession: (data) => {
    return fetchData({
      url: 'retrieval/faq-import/session',
      method: 'POST',
      data,
    });
  },

  parseFaqImport: (sessionId, rawText) => {
    return fetchData({
      url: `retrieval/faq-import/${sessionId}/parse`,
      method: 'POST',
      data: { rawText },
    });
  },

  updateParsedPairs: (sessionId, parsedPairs) => {
    return fetchData({
      url: `retrieval/faq-import/${sessionId}/parsed-pairs`,
      method: 'PUT',
      data: { parsedPairs },
    });
  },

  analyzeFaqImport: (sessionId) => {
    return fetchData({
      url: `retrieval/faq-import/${sessionId}/analyze`,
      method: 'POST',
    });
  },

  updateFaqDrafts: (sessionId, faqDrafts) => {
    return fetchData({
      url: `retrieval/faq-import/${sessionId}/faq-drafts`,
      method: 'PUT',
      data: { faqDrafts },
    });
  },

  updateConfigDraft: (sessionId, payload) => {
    return fetchData({
      url: `retrieval/faq-import/${sessionId}/config-draft`,
      method: 'PUT',
      data: payload,
    });
  },

  applyFaqImport: (sessionId, payload) => {
    return fetchData({
      url: `retrieval/faq-import/${sessionId}/apply`,
      method: 'POST',
      data: payload,
    });
  },

  // ==================== ARCHITECT ENDPOINTS ====================

  /**
   * Create agent via Architect (Quick Mode)
   * POST /retrieval/agents/architect
   */
  createAgentArchitect: (data) => {
    return fetchData({
      url: 'retrieval/agents/architect',
      method: 'POST',
      data,
    });
  },

  /**
   * Poll Agent Status
   * GET /retrieval/agents/:id/status
   */
  getAgentStatus: (id) => {
    return fetchData({
      url: `retrieval/agents/${id}/status`,
      method: 'GET',
    });
  },

  /**
   * Get chat history
   * GET /retrieval/architect/:agentId/history
   */
  getArchitectHistory: (agentId, sessionId) => {
    let url = `retrieval/architect/${agentId}/history`;
    if (sessionId) url += `?sessionId=${sessionId}`;
    return fetchData({
      url,
      method: 'GET',
    });
  },

  /**
   * Clear chat session
   * DELETE /retrieval/architect/session/:sessionId
   */
  clearArchitectSession: (sessionId) => {
    return fetchData({
      url: `retrieval/architect/session/${sessionId}`,
      method: 'DELETE',
    });
  },

  /**
   * Helper to get streaming base URL
   */
  getStreamingUrl: (path) => {
    const baseUrl = process.env.REACT_APP_RAG_SERVICE_URL || process.env.REACT_APP_API_CHOTSALE_URL || window.location.origin;
    return `${baseUrl}/retrieval/${path}`;
  },
};

export default aiConfigApi;
